import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Search, Filter, Video, BookOpen, Award, Book,
  Calendar, Users, Clock, Eye, Edit, Trash2, MoreVertical,
  ChevronDown, CheckCircle, XCircle, Play, Upload, Youtube,
  Mail, Download, Settings
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPE_ICONS = {
  curso: BookOpen,
  webinar: Video,
  masterclass: Award,
  diplomado: Book,
  taller: BookOpen,
  ponencia: Video
};

const CONTENT_TYPE_COLORS = {
  curso: 'bg-blue-100 text-blue-700',
  webinar: 'bg-green-100 text-green-700',
  masterclass: 'bg-purple-100 text-purple-700',
  diplomado: 'bg-orange-100 text-orange-700',
  taller: 'bg-amber-100 text-amber-700',
  ponencia: 'bg-cyan-100 text-cyan-700'
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-yellow-100 text-yellow-700',
  live: 'bg-red-100 text-red-700',
  recorded: 'bg-blue-100 text-blue-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-500'
};

export default function AdminEducationalContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [contentTypes, setContentTypes] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    content_type: 'webinar',
    access_level: 'affiliate',
    is_free: false,
    price: 0,
    category: '',
    level: '',
    duration_minutes: 60,
    has_certificate: false,
    scheduled_start: ''
  });
  const [importData, setImportData] = useState({
    video_id: '',
    content_type: 'webinar',
    is_free: false,
    category: ''
  });
  const [videoPreview, setVideoPreview] = useState(null);
  const [error, setError] = useState('');

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadContentTypes();
    loadContent();
  }, [navigate, token, filterType, filterStatus]);

  const loadContentTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/educational/content-types`);
      setContentTypes(response.data);
    } catch (error) {
      console.error('Error loading content types:', error);
    }
  };

  const loadContent = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType) params.append('content_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      params.append('limit', '50');

      const response = await axios.get(`${API_URL}/api/educational/catalog?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = {
        ...formData,
        price: formData.is_free ? 0 : parseFloat(formData.price),
        scheduled_start: formData.scheduled_start ? new Date(formData.scheduled_start).toISOString() : null
      };

      await axios.post(`${API_URL}/api/educational/content`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        short_description: '',
        content_type: 'webinar',
        access_level: 'affiliate',
        is_free: false,
        price: 0,
        category: '',
        level: '',
        duration_minutes: 60,
        has_certificate: false,
        scheduled_start: ''
      });
      loadContent();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear contenido');
    }
  };

  const handleImportVideo = async () => {
    if (!importData.video_id) {
      setError('Ingresa el ID del video de YouTube');
      return;
    }

    try {
      const form = new FormData();
      form.append('content_type', importData.content_type);
      form.append('is_free', importData.is_free);
      if (importData.category) form.append('category', importData.category);

      await axios.post(
        `${API_URL}/api/educational/youtube/import/${importData.video_id}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowImportModal(false);
      setImportData({ video_id: '', content_type: 'webinar', is_free: false, category: '' });
      setVideoPreview(null);
      loadContent();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al importar video');
    }
  };

  const previewYouTubeVideo = async () => {
    if (!importData.video_id) return;

    try {
      const response = await axios.get(
        `${API_URL}/api/educational/youtube/video/${importData.video_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVideoPreview(response.data);
      setError('');
    } catch (error) {
      setError('No se pudo obtener información del video');
      setVideoPreview(null);
    }
  };

  const extractVideoId = (url) => {
    // Extract video ID from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^[a-zA-Z0-9_-]{11}$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1] || match[0];
    }
    return url;
  };

  const filteredItems = items.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.speaker_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-educational-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Book className="w-7 h-7 text-indigo-500" />
              Contenido Educativo
            </h1>
            <p className="text-gray-500 text-sm">Cursos, webinars, master classes y diplomados</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
              data-testid="import-youtube-btn"
            >
              <Youtube className="w-4 h-4" />
              Importar de YouTube
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
              data-testid="create-content-btn"
            >
              <Plus className="w-4 h-4" />
              Crear Contenido
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{items.length}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
            </div>
          </div>
          {['webinar', 'masterclass', 'curso', 'diplomado'].map(type => {
            const Icon = CONTENT_TYPE_ICONS[type];
            const count = items.filter(i => i.content_type === type).length;
            return (
              <div key={type} className="bg-white p-4 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${CONTENT_TYPE_COLORS[type]?.split(' ')[0]}`}>
                    <Icon className={`w-5 h-5 ${CONTENT_TYPE_COLORS[type]?.split(' ')[1]}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-sm text-gray-500 capitalize">{type}s</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por título o ponente..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="search-content-input"
              />
            </div>
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todos los tipos</option>
            <option value="webinar">Webinar</option>
            <option value="masterclass">Master Class</option>
            <option value="curso">Curso</option>
            <option value="diplomado">Diplomado</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="scheduled">Programado</option>
            <option value="live">En vivo</option>
            <option value="published">Publicado</option>
            <option value="archived">Archivado</option>
          </select>
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando contenido...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <Book className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No hay contenido educativo</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Crear el primero
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map(item => {
              const Icon = CONTENT_TYPE_ICONS[item.content_type] || Video;
              return (
                <div key={item.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-gray-100">
                    {item.thumbnail_url ? (
                      <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
                        <Icon className="w-12 h-12 text-white/30" />
                      </div>
                    )}
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium ${CONTENT_TYPE_COLORS[item.content_type]}`}>
                      {item.content_type_info?.name || item.content_type}
                    </div>
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[item.status]}`}>
                      {item.status}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2">{item.title}</h3>
                    {item.speaker_name && (
                      <p className="text-sm text-gray-500 mb-2">{item.speaker_name}</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-sm text-gray-500 mb-3">
                      {item.scheduled_start && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(item.scheduled_start).toLocaleDateString('es-MX')}
                        </span>
                      )}
                      {item.duration_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {item.duration_minutes}min
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {item.total_registrations || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div>
                        {item.is_free ? (
                          <span className="text-green-600 font-medium">Gratis</span>
                        ) : (
                          <span className="font-bold text-gray-900">
                            ${item.price?.toLocaleString()} MXN
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/masterclasses/${item.slug || item.id}`)}
                          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Ver"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Crear Contenido Educativo</h3>
                  <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
              </div>

              <form onSubmit={handleCreate} className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Contenido *</label>
                    <select
                      value={formData.content_type}
                      onChange={(e) => {
                        const type = e.target.value;
                        const isMasterclass = type === 'masterclass';
                        setFormData({ 
                          ...formData, 
                          content_type: type,
                          is_free: isMasterclass ? true : formData.is_free,
                          price: isMasterclass ? 0 : formData.price,
                          access_level: isMasterclass ? 'free' : formData.access_level
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="webinar">Webinar ($500 - $2,000 MXN)</option>
                      <option value="masterclass">Master Class (GRATIS - Regalo)</option>
                      <option value="taller">Taller ($800 - $3,000 MXN)</option>
                      <option value="ponencia">Ponencia ($300 - $1,000 MXN)</option>
                      <option value="curso">Curso ($2,000 - $15,000 MXN)</option>
                      <option value="diplomado">Diplomado ($15,000 - $50,000 MXN)</option>
                    </select>
                    {formData.content_type === 'masterclass' && (
                      <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Las Master Classes son siempre GRATIS
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Seleccionar...</option>
                      {contentTypes?.categories?.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nivel</label>
                    <select
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Seleccionar...</option>
                      {contentTypes?.levels?.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duración (minutos)</label>
                    <input
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha y Hora</label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_start}
                      onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Acceso</label>
                    <select
                      value={formData.access_level}
                      onChange={(e) => setFormData({ ...formData, access_level: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="free">Gratuito</option>
                      <option value="affiliate">Solo Afiliados</option>
                      <option value="vip">Solo VIP</option>
                      <option value="premium">Compra Adicional</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.is_free}
                        onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Contenido gratuito</span>
                    </label>
                  </div>

                  {!formData.is_free && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Precio (MXN)</label>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.has_certificate}
                        onChange={(e) => setFormData({ ...formData, has_certificate: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Incluye certificado</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
                  >
                    Crear Contenido
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import YouTube Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Youtube className="w-5 h-5 text-red-500" />
                    Importar Video de YouTube
                  </h3>
                  <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL o ID del Video *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={importData.video_id}
                      onChange={(e) => setImportData({ ...importData, video_id: extractVideoId(e.target.value) })}
                      placeholder="https://youtube.com/watch?v=... o ID del video"
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <button
                      type="button"
                      onClick={previewYouTubeVideo}
                      className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                    >
                      Vista previa
                    </button>
                  </div>
                </div>

                {videoPreview && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex gap-4">
                      {videoPreview.thumbnail_url && (
                        <img
                          src={videoPreview.thumbnail_url}
                          alt={videoPreview.title}
                          className="w-32 h-20 object-cover rounded"
                        />
                      )}
                      <div>
                        <p className="font-medium text-gray-900 line-clamp-2">{videoPreview.title}</p>
                        <p className="text-sm text-gray-500">{videoPreview.channel_title}</p>
                        <p className="text-sm text-gray-500">{videoPreview.duration_formatted}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Contenido</label>
                  <select
                    value={importData.content_type}
                    onChange={(e) => setImportData({ ...importData, content_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="webinar">Webinar</option>
                    <option value="masterclass">Master Class</option>
                    <option value="curso">Curso</option>
                    <option value="diplomado">Diplomado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                  <select
                    value={importData.category}
                    onChange={(e) => setImportData({ ...importData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Seleccionar...</option>
                    {contentTypes?.categories?.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={importData.is_free}
                    onChange={(e) => setImportData({ ...importData, is_free: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Contenido gratuito</span>
                </label>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleImportVideo}
                    disabled={!importData.video_id}
                    className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300"
                  >
                    Importar Video
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
